import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Stack, Box, styled } from '@mui/material';
import type { BaseAutocompleteValue, BaseAutocompleteProps } from '../../types';
import { AutocompleteChip, LimitCountChip } from './';

/**
 * Layout constants for chip visibility calculation.
 */
const CHIP_WIDTH = 140; // Assumed chip width for visibility calculation
const CHIP_MAX_WIDTH = 300; // Maximum chip width when redistributing space
const COUNT_CHIP_WIDTH = 50; // Width reserved for the "+N" indicator
const CHIP_GAP = 4; // Gap between chips
const INPUT_MIN_WIDTH = 60; // Minimum width reserved for the input field
const END_ADORNMENT_WIDTH = 72; // Approximate width of clear + dropdown buttons

/**
 * Styled container for chips with CSS-based overflow handling.
 * Uses flex: 0 1 auto so it only takes the space needed for its content.
 */
const ChipsContainer = styled(Stack)({
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  alignItems: 'center',
  flexWrap: 'nowrap',
});

/**
 * Inner container that holds the visible chips.
 * Uses overflow hidden to clip chips that don't fit.
 */
const ChipsWrapper = styled(Box)({
  display: 'flex',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  flex: '0 1 auto',
  minWidth: 0,
  gap: CHIP_GAP,
});

/**
 * Container for the count chip - never shrinks or gets clipped.
 */
const CountChipWrapper = styled(Box)({
  flexShrink: 0,
  marginLeft: CHIP_GAP,
});

export interface BaseAutocompleteChipsProps {
  /** Selected values to render as chips */
  values: BaseAutocompleteValue[];
  /** Function to get tag props from MUI Autocomplete */
  getTagProps: (params: { index: number }) => Record<string, unknown>;
  /** Translation function */
  t: (key: string, options?: Record<string, string | number>) => string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Function to map option to value format */
  mapOptionToValue: BaseAutocompleteProps['mapOptionToValue'];
  /** Callback to toggle the dropdown open state */
  onToggleOpen: () => void;
}

/**
 * Calculates how many chips can fit and their optimal width.
 * Uses CHIP_WIDTH (140px) for visibility calculation, then redistributes space.
 *
 * @param availableWidth - Total available width in pixels for chips
 * @param totalChips - Total number of chips to display
 * @returns Object with visibleCount, chipWidth, and whether to show count chip
 */
const calculateVisibleChips = (availableWidth: number, totalChips: number) => {
  if (totalChips === 0) {
    return { visibleCount: 0, showCountChip: false, chipWidth: CHIP_WIDTH };
  }

  // Fallback for initial render before measurement
  if (availableWidth === 0) {
    return { visibleCount: 1, showCountChip: totalChips > 1, chipWidth: CHIP_WIDTH };
  }

  const chipWithGap = CHIP_WIDTH + CHIP_GAP;
  const allChipsWidth = totalChips * chipWithGap - CHIP_GAP; // No gap after last chip

  // All chips fit - redistribute available space among them
  if (allChipsWidth <= availableWidth) {
    const totalGaps = (totalChips - 1) * CHIP_GAP;
    const widthPerChip = Math.min(CHIP_MAX_WIDTH, (availableWidth - totalGaps) / totalChips);
    return { visibleCount: totalChips, showCountChip: false, chipWidth: widthPerChip };
  }

  // Need count chip - calculate how many chips fit using standard width
  const availableForChips = availableWidth - COUNT_CHIP_WIDTH - CHIP_GAP;
  const fittingChips = Math.max(0, Math.floor((availableForChips + CHIP_GAP) / chipWithGap));

  if (fittingChips >= 1) {
    // Redistribute remaining space among visible chips
    const totalGaps = (fittingChips - 1) * CHIP_GAP;
    const widthPerChip = Math.min(CHIP_MAX_WIDTH, (availableForChips - totalGaps) / fittingChips);
    return { visibleCount: fittingChips, showCountChip: true, chipWidth: Math.max(CHIP_WIDTH, widthPerChip) };
  }

  // No chips fit - show only count chip
  return { visibleCount: 0, showCountChip: true, chipWidth: CHIP_WIDTH };
};

/**
 * Custom hook for measuring available width for chips.
 * Uses a callback ref to react when the element is attached to the DOM.
 */
const useAvailableWidth = () => {
  const [width, setWidth] = useState(0);
  const [element, setElement] = useState<HTMLElement | null>(null);

  // Callback ref - React calls this when the element is attached/detached
  const callbackRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    // Find the MuiFilledInput-root parent container
    const inputRoot = element.closest('.MuiInputBase-root');
    if (!inputRoot) return;

    const calculateAvailableWidth = () => {
      const parentWidth = inputRoot.clientWidth;
      // Subtract: padding (24px), input min width, end adornment, and some buffer
      const available = parentWidth - INPUT_MIN_WIDTH - END_ADORNMENT_WIDTH - 24;
      setWidth(Math.max(0, available));
    };

    calculateAvailableWidth();

    // Use ResizeObserver for efficient width tracking
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(calculateAvailableWidth);
      observer.observe(inputRoot);
      return () => observer.disconnect();
    }

    // Fallback for environments without ResizeObserver
    window.addEventListener('resize', calculateAvailableWidth);
    return () => window.removeEventListener('resize', calculateAvailableWidth);
  }, [element]);

  return { width, callbackRef };
};

/**
 * BaseAutocompleteChips renders chips for multi-select autocomplete.
 *
 * Features:
 * - Automatically shows as many chips as fit in available space
 * - Displays "+N" indicator for hidden chips
 * - Uses CSS for layout, JavaScript only for width measurement
 * - Returns null when no values (takes no space)
 *
 * @see https://mui.com/material-ui/react-autocomplete/#limit-tags
 */
export const BaseAutocompleteChips: React.FC<BaseAutocompleteChipsProps> = ({
  values,
  getTagProps,
  t,
  disabled,
  mapOptionToValue,
  onToggleOpen,
}) => {
  const { width: availableWidth, callbackRef } = useAvailableWidth();

  const { visibleCount, showCountChip, chipWidth } = useMemo(
    () => calculateVisibleChips(availableWidth, values.length),
    [availableWidth, values.length],
  );

  // Don't render anything if no values - takes no space
  if (values.length === 0) {
    return null;
  }

  const visibleValues = values.slice(0, visibleCount);
  const hiddenCount = values.length - visibleCount;

  return (
    <ChipsContainer ref={callbackRef} direction="row" data-testid="chips-container">
      {visibleCount > 0 && (
        <ChipsWrapper>
          {visibleValues.map((value, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Box key={index} sx={{ flexShrink: 0, maxWidth: chipWidth }}>
                <AutocompleteChip index={index} value={mapOptionToValue(value)} itemProps={tagProps} />
              </Box>
            );
          })}
        </ChipsWrapper>
      )}

      {showCountChip && hiddenCount > 0 && (
        <CountChipWrapper>
          <LimitCountChip disabled={disabled} onClick={onToggleOpen} numberOfHiddenValues={hiddenCount} t={t} />
        </CountChipWrapper>
      )}
    </ChipsContainer>
  );
};
